import React, { useState, useEffect, useCallback, useRef } from "react";
import { useEmblaCarousel } from "embla-carousel/react";
import styled from "@emotion/styled";
import { PrevButton, NextButton } from "./CarouselButtons";
import { css } from "@emotion/react";
import useWindowSize from "react-use/lib/useWindowSize";
import { Tabs, Tab } from "../DeprecatedTabs";

export enum CarouselDotSize {
  SMALL = "small",
}

export enum ButtonPositions {
  BOTTOM = "bottom",
  CENTRE = "centre",
}

type CarouselProps = {
  onSlideChanged?: (index: number) => void;
  arrowNavigation?: boolean;
  buttonBgColor?: string;
  buttonPosition?: ButtonPositions;
  carouselWidth?: number;
  dotBgColor?: string;
  dotSize?: CarouselDotSize;
  isDotNavVisible?: boolean;
  multislideView?: boolean;
  paddingTopOverride?: string;
  slides: Slide[];
  cardWidth?: number; //custom card width for multislide carousel
  infiniteLoop?: boolean;
  carouselSubtitle?: React.ReactElement;
  isNotDraggable?: boolean;
  activeModalIndex?: number | null;
  tabs?: Tab[] | null;
  onSelectTab?: (id: any) => void;
  selectedTab?: string;
  insetButtons?: boolean;
  navigationSeparatorHeight?: number;
  slug?: string | string[];
};

type Slide = React.ReactChild | React.ReactElement | any;

type DotButtonProps = {
  bgColor?: string;
  selected?: boolean;
  size?: CarouselDotSize;
  infiniteLoop?: boolean;
};

const DotNavigation = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  padding-top: 16px;
  width: 100%;
`;

const DotButton = styled.button<DotButtonProps>`
  background-color: ${({ theme, selected, bgColor }) =>
    selected ? theme.colors.jungle : bgColor || "#919191"};
  height: ${({ size }) => (size === CarouselDotSize.SMALL ? "6px" : "12px")};
  width: ${({ size }) => (size === CarouselDotSize.SMALL ? "6px" : "12px")};
  border-radius: 50%;
  border: 0;
  padding: 0;
  cursor: pointer;
  margin-right: ${({ size }) =>
    size === CarouselDotSize.SMALL ? "6px" : "16px"};

  &:first-of-type {
    margin-left: 16px;
  }
`;

const Embla = styled.div`
  position: relative;
  max-width: 100%;
  margin-left: auto;
  margin-right: auto;
  cursor: pointer;

  .is-draggable {
    cursor: move;
    cursor: grab;
  }
  .is-dragging {
    cursor: grabbing;
  }
`;

const EmblaViewport = styled.div`
  overflow: hidden;
  width: 100%;
`;

const NavigationSeparator = styled.div<{ navigationSeparatorHeight?: number }>`
  width: 100%;
  height: ${(props) =>
    props.navigationSeparatorHeight
      ? `${props.navigationSeparatorHeight}px`
      : "24px"};
`;

const EmblaContainer = styled.div<{ carouselWidth?: number }>`
  display: flex;
  align-content: stretch;

  user-select: none;
  -webkit-touch-callout: none;
  -khtml-user-select: none;
  -webkit-tap-highlight-color: transparent;

  max-width: ${(props) =>
    props.carouselWidth ? `${props.carouselWidth}px` : "unset"};
`;

const EmblaSlide = styled.div<{ cardWidth?: number }>`
  position: relative;
  min-width: 100%;
  margin-left: 24px;

  min-width: ${(props) => (props.cardWidth ? `${props.cardWidth}px` : "100%")};
`;

const EmblaSlideInner = styled.div<{ multislide?: boolean }>`
  display: flex;
  justify-content: center;
  align-items: flex-start;

  position: relative;
  min-width: 100%;
  height: 100%;

  ${(props) =>
    props.multislide &&
    css`
      overflow: hidden;
    `}
`;

const EmblaArrowNavigationSlideInner = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
`;

const ButtonsContainer = styled.div<{
  buttonPosition?: ButtonPositions;
  insetButtons?: boolean;
}>`
  position: absolute;
  display: flex;
  justify-content: space-between;
  width: 100%;
  padding: ${(props) => (props.insetButtons ? "0px 30px" : "0px")};

  @media ${(props) => props.theme.bp.tabletUp} {
    padding: ${(props) => (props.insetButtons ? "0px 30px" : "0px")};
  }

  @media ${(props) => props.theme.bp.desktopUp} {
    padding: 0px;
  }

  ${({ buttonPosition }) =>
    buttonPosition === ButtonPositions.CENTRE &&
    css`
      top: 50%;
      transform: translateY(-50%);
      margin-bottom: 31px;
    `}
`;

const ArrowNavigationWrapper = styled.div`
  display: flex;
  justify-content: center;
  width: 100%;
`;

const Subtitle = styled.div`
  text-align: center;
`;

export const EmblaCarousel = ({
  onSlideChanged,
  arrowNavigation,
  buttonBgColor,
  buttonPosition = ButtonPositions.BOTTOM,
  carouselWidth,
  dotBgColor,
  dotSize,
  isDotNavVisible = true,
  multislideView,
  slides,
  cardWidth,
  infiniteLoop,
  carouselSubtitle,
  isNotDraggable,
  activeModalIndex,
  tabs,
  onSelectTab,
  selectedTab,
  insetButtons,
  navigationSeparatorHeight,
  slug,
}: CarouselProps) => {
  const [viewportRef, embla] = useEmblaCarousel({
    containScroll: multislideView ? "trimSnaps" : "",
    loop: infiniteLoop ? true : false,
    draggable: isNotDraggable ? false : true,
    startIndex:
      activeModalIndex && activeModalIndex !== null ? activeModalIndex : 0,
  });
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [prevBtnEnabled, setPrevBtnEnabled] = useState(false);
  const [nextBtnEnabled, setNextBtnEnabled] = useState(false);

  const scrollPrev = useCallback(() => embla && embla.scrollPrev(), [embla]);
  const scrollNext = useCallback(() => embla && embla.scrollNext(), [embla]);
  const scrollTo = useCallback(
    (index) => embla && embla.scrollTo(index),
    [embla]
  );

  // this handles updating the selected index for dot navigation & arrow navigation
  const onSelect = useCallback(() => {
    if (embla) {
      onSlideChanged?.(embla.selectedScrollSnap());
      setSelectedIndex(embla.selectedScrollSnap());
      setPrevBtnEnabled(embla.canScrollPrev());
      setNextBtnEnabled(embla.canScrollNext());
    }
  }, [embla, setSelectedIndex]);

  useEffect(() => {
    if (embla) {
      onSelect();
      embla.on("select", onSelect);
    }
  }, [embla, onSelect]);

  const slideRef = useRef<HTMLDivElement>(null);

  const [arrowsWidth, setArrowsWidth] = useState(0);

  const { width } = useWindowSize();

  useEffect(() => {
    setArrowsWidth(slideRef.current?.offsetWidth || 0);
  }, [slideRef, slides, width]);

  return (
    <Embla>
      {tabs && selectedTab && onSelectTab && (
        <Tabs
          tabs={tabs}
          active={selectedTab}
          onSelectTab={(_, id: any) => onSelectTab(id)}
        />
      )}
      <EmblaViewport ref={viewportRef}>
        <EmblaContainer
          ref={slideRef}
          carouselWidth={carouselWidth}
          id="embla-container"
        >
          {slides.map((slide: Slide, index: number) => (
            <EmblaSlide
              key={index}
              cardWidth={cardWidth}
              className="embla-item"
            >
              <EmblaSlideInner
                ref={multislideView ? slideRef : null}
                multislide={multislideView}
              >
                {arrowNavigation || slug === "impact-shop" ? (
                  <EmblaArrowNavigationSlideInner
                    ref={!multislideView ? slideRef : null}
                  >
                    {slide}
                  </EmblaArrowNavigationSlideInner>
                ) : (
                  <>{slide}</>
                )}
              </EmblaSlideInner>
            </EmblaSlide>
          ))}
        </EmblaContainer>
        <Subtitle>{carouselSubtitle}</Subtitle>

        {isDotNavVisible && (
          <NavigationSeparator
            navigationSeparatorHeight={navigationSeparatorHeight}
          />
        )}

        {arrowNavigation && arrowsWidth && (
          <ArrowNavigationWrapper>
            <ButtonsContainer
              buttonPosition={buttonPosition}
              style={{ maxWidth: arrowsWidth }}
              insetButtons={insetButtons}
            >
              <PrevButton
                onClick={scrollPrev}
                enabled={prevBtnEnabled}
                bgColor={buttonBgColor}
              />
              <NextButton
                onClick={scrollNext}
                enabled={nextBtnEnabled}
                bgColor={buttonBgColor}
              />
            </ButtonsContainer>
          </ArrowNavigationWrapper>
        )}

        {isDotNavVisible && (
          <DotNavigation>
            {slides.map((_: Slide, index: number) => {
              return (
                <DotButton
                  bgColor={dotBgColor}
                  key={index}
                  onClick={() => {
                    scrollTo(index);
                    setSelectedIndex(index);
                  }}
                  selected={index === selectedIndex}
                  size={dotSize}
                />
              );
            })}
          </DotNavigation>
        )}
      </EmblaViewport>
    </Embla>
  );
};

const WithArrowNavigation = (props: CarouselProps) => {
  return <EmblaCarousel arrowNavigation {...props} />;
};

const Multislide = (props: CarouselProps) => {
  return <EmblaCarousel multislideView arrowNavigation {...props} />;
};

EmblaCarousel.WithArrowNavigation = WithArrowNavigation;
EmblaCarousel.Multislide = Multislide;

export default EmblaCarousel;
